import { NavLink } from 'react-router'
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  Building2,
  Bug,
  CalendarClock,
  FileText,
  LogOut,
  PhoneCall,
  Radio,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLiveCalls } from '@/hooks/queries/useAdmin'
import { cn } from '@/lib/utils'

interface NavEntry {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
}

export const NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: BarChart3, end: true },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/enterprises', label: 'Enterprises', icon: Building2 },
  { to: '/calls', label: 'Calls', icon: PhoneCall },
  { to: '/live', label: 'Live', icon: Radio },
  { to: '/scheduled', label: 'Scheduled', icon: CalendarClock },
  { to: '/logs', label: 'Logs', icon: ScrollText },
  { to: '/templates', label: 'Templates', icon: FileText },
  { to: '/settings', label: 'Settings', icon: Settings },
]

/** Shown only to superadmins — the tools behind it can rewrite any record. */
const SUPERADMIN_NAV: NavEntry[] = [{ to: '/debug', label: 'Debug', icon: Bug }]

/**
 * The nav list, shared by the desktop sidebar and the mobile drawer so the two
 * cannot drift apart. `onNavigate` lets the drawer close itself on selection.
 */
export function NavItems({ onNavigate, role }: { onNavigate?: () => void; role?: string }) {
  // Badge the Live tab so an in-progress call is visible from any page. Uses
  // the server's total, not the page length, so it cannot disagree with the
  // dashboard's count.
  const { data: live } = useLiveCalls()
  const liveTotal = live?.total ?? 0

  return (
    <nav className="flex-1 space-y-0.5 px-2">
      {[...NAV, ...(role === 'superadmin' ? SUPERADMIN_NAV : [])].map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              // min-h-11 keeps every row at a comfortable touch target on mobile
              'flex min-h-11 items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors md:min-h-0',
              isActive
                ? 'bg-primary/10 font-medium text-primary'
                : 'hover:bg-accent hover:text-accent-foreground',
            )
          }
        >
          <Icon className="size-4 shrink-0" />
          {label}
          {to === '/live' && liveTotal > 0 && (
            <span className="ml-auto flex min-w-5 items-center justify-center rounded-full bg-live px-1 text-[10px] font-semibold text-white">
              {liveTotal > 999 ? '999+' : liveTotal}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  )
}

/** Account row + theme/sign-out controls, shared by sidebar and drawer. */
export function NavFooter({
  email,
  role,
  onSignOut,
  theme,
}: {
  email?: string
  role?: string
  onSignOut: () => void
  theme: ReactNode
}) {
  return (
    <div className="space-y-2 border-t p-3">
      <div className="min-w-0 px-1">
        <div className="truncate text-xs font-medium">{email}</div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{role}</div>
      </div>
      <div className="flex gap-1">
        {theme}
        <Button variant="ghost" size="icon-sm" title="Sign out" aria-label="Sign out" onClick={onSignOut}>
          <LogOut className="size-4" />
        </Button>
      </div>
    </div>
  )
}
